/** Bounded SSE reader. EOF without a protocol terminal event is handled by the caller. */
function assertSseResponse(response: Response): asserts response is Response & {
  body: ReadableStream<Uint8Array>;
} {
  if (!response.ok) throw new Error("Text stream unavailable");
  if (!response.body) throw new Error("Text stream unavailable");
  if (!response.headers.get("content-type")?.startsWith("text/event-stream"))
    throw new Error("Text stream unavailable");
}

function nextSseFrame(buffer: string) {
  const boundary = /\r?\n\r?\n/.exec(buffer);
  if (!boundary) return null;
  return {
    frame: buffer.slice(0, boundary.index),
    remainder: buffer.slice(boundary.index + boundary[0].length),
  };
}

function sseData(frame: string): string {
  return frame
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).replace(/^ /, ""))
    .join("\n");
}

export async function* readSseData(response: Response, signal: AbortSignal, maxBytes = 262_144) {
  assertSseResponse(response);
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let buffer = "";
  let bytes = 0;
  const abort = () => {
    void reader.cancel().catch(() => undefined);
  };
  signal.addEventListener("abort", abort, { once: true });
  try {
    while (true) {
      signal.throwIfAborted();
      const { done, value } = await reader.read();
      signal.throwIfAborted();
      if (done) {
        buffer += decoder.decode();
        if (buffer.trim()) throw new Error("Truncated text stream");
        return;
      }
      bytes += value.byteLength;
      if (bytes > maxBytes) throw new Error("Text stream too large");
      buffer += decoder.decode(value, { stream: true });
      let next = nextSseFrame(buffer);
      while (next) {
        buffer = next.remainder;
        const data = sseData(next.frame);
        if (data) yield data;
        next = nextSseFrame(buffer);
      }
    }
  } finally {
    signal.removeEventListener("abort", abort);
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

export const DOCUMENT_EXECUTION_RECOVERY =
  "Creation outcome needs verification. Check the proposal receipt or retry only the same proposal revision. Do not create a duplicate.";
export const DOCUMENT_EXECUTION_REJECTED =
  "Document creation was rejected. No document was created. Review the proposal before trying again.";

export class DocumentExecutionRejectedError extends Error {
  constructor() {
    super(DOCUMENT_EXECUTION_REJECTED);
    this.name = "DocumentExecutionRejectedError";
  }
}

export class DocumentExecutionUnconfirmedError extends Error {
  constructor() {
    super(DOCUMENT_EXECUTION_RECOVERY);
    this.name = "DocumentExecutionUnconfirmedError";
  }
}

export const BUSINESS_OPERATION_EXECUTION_RECOVERY =
  "Operation outcome needs verification. Check the command receipt or retry only the same command. Do not issue a duplicate.";

export class BusinessOperationExecutionUnconfirmedError extends Error {
  constructor() {
    super(BUSINESS_OPERATION_EXECUTION_RECOVERY);
    this.name = "BusinessOperationExecutionUnconfirmedError";
  }
}

export type TextStreamEvent =
  | { type: "delta"; text: string }
  | { type: "reset" }
  | { type: "error" }
  | {
      type: "assistantDocumentExecution";
      conversationId: string;
      proposalId: string;
      revision: number;
      status: "started" | "created" | "unknown" | "rejected";
      documentId?: string;
      documentKind?: string;
    }
  | {
      type: "assistantBusinessExecution";
      conversationId: string;
      commandId: string;
      status: "started" | "created" | "unknown" | "rejected";
      operation?: string;
      resource?: { kind?: string; id?: string };
      /** A rejected attempt's code; EXECUTION_CONFLICT rolled back and is safe to retry. */
      errorCode?: string;
    }
  | {
      type: "final";
      conversationId: string;
      messageId: string;
      messageEnvelope: string;
      text: string;
      isFinal: true;
    }
  | {
      type: "assistantDocumentDraft";
      conversationId: string;
      assistantDocumentDraft: unknown;
    };

type DocumentExecutionEvent = Extract<TextStreamEvent, { type: "assistantDocumentExecution" }>;
type BusinessExecutionEvent = Extract<TextStreamEvent, { type: "assistantBusinessExecution" }>;
type PendingExecution = "business" | "document";
type ExecutionState = {
  committedBusiness: boolean;
  committedDocument: boolean;
  // A coded rejection created nothing; the model's answer explains it, and
  // only a turn that ends without one reports the rejection itself.
  rejectedDocument: boolean;
  pending: Map<string, PendingExecution>;
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Registry name shapes. The edge declares each turn's operations from the
// registry and SQL binds every receipt's kind, so a session snapshot here
// would only go stale.
const OPERATION_NAME = /^[a-z]+(_[a-z]+)*\.[a-z]+(_[a-z]+)*$/;
const RESOURCE_KIND = /^[a-z]+(_[a-z]+)*$/;
const ERROR_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;

function assertTextFrame(condition: unknown): asserts condition {
  if (!condition) throw new Error("Incomplete text answer");
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function string(record: Record<string, unknown> | null, key: string): string | null {
  const value = record?.[key];
  return typeof value === "string" && value ? value : null;
}

function startExecution(state: ExecutionState, key: string, kind: PendingExecution): void {
  state.pending.set(key, kind);
  if (state.pending.size > 10)
    throw kind === "document"
      ? new DocumentExecutionUnconfirmedError()
      : new BusinessOperationExecutionUnconfirmedError();
}

function documentExecutionKey(event: DocumentExecutionEvent, conversationId: string): string {
  assertTextFrame(event.conversationId === conversationId);
  assertTextFrame(UUID.test(event.proposalId));
  assertTextFrame(Number.isSafeInteger(event.revision) && event.revision >= 1);
  return `${event.proposalId}:${event.revision}`;
}

function confirmDocumentExecution(
  event: DocumentExecutionEvent,
  state: ExecutionState,
  key: string,
): void {
  assertTextFrame(state.pending.get(key) === "document");
  if (event.status === "created") {
    assertTextFrame(UUID.test(event.documentId ?? ""));
    assertTextFrame(["estimate", "invoice", "purchase_order"].includes(event.documentKind ?? ""));
    state.committedDocument = true;
  }
  state.pending.delete(key);
  if (event.status === "rejected") state.rejectedDocument = true;
}

function trackDocumentExecution(
  event: DocumentExecutionEvent,
  conversationId: string,
  state: ExecutionState,
): void {
  const key = documentExecutionKey(event, conversationId);
  if (event.status === "started") return startExecution(state, key, "document");
  if (event.status === "unknown") throw new DocumentExecutionUnconfirmedError();
  if (event.status === "created" || event.status === "rejected")
    return confirmDocumentExecution(event, state, key);
  throw new Error("Incomplete text answer");
}

function businessOperation(event: BusinessExecutionEvent): string | null {
  return typeof event.operation === "string" && OPERATION_NAME.test(event.operation)
    ? event.operation
    : null;
}

function assertBusinessResource(input: { operation: string | null; resource: unknown }): void {
  const resource = record(input.resource);
  assertTextFrame(input.operation !== null);
  assertTextFrame(RESOURCE_KIND.test(string(resource, "kind") ?? ""));
  assertTextFrame(UUID.test(string(resource, "id") ?? ""));
}

/** A validated business frame: command identity, outcome, and receipt resource only. */
export interface BusinessExecutionFrame {
  commandId: string;
  status: BusinessExecutionEvent["status"];
  operation?: string;
  resource?: { kind: string; id: string };
  errorCode?: string;
}

function businessFrame(event: BusinessExecutionEvent): BusinessExecutionFrame {
  const operation = businessOperation(event);
  const resource = record(event.resource);
  const kind = string(resource, "kind");
  const id = string(resource, "id");
  const validCode = typeof event.errorCode === "string" && ERROR_CODE.test(event.errorCode);
  return {
    commandId: event.commandId,
    status: event.status,
    ...(operation ? { operation } : {}),
    ...(event.status === "created" && kind && id ? { resource: { kind, id } } : {}),
    ...(event.status === "rejected" && validCode ? { errorCode: event.errorCode } : {}),
  };
}

function trackBusinessExecution(
  event: BusinessExecutionEvent,
  conversationId: string,
  state: ExecutionState,
  onExecution: (frame: BusinessExecutionFrame) => void,
): void {
  assertTextFrame(event.conversationId === conversationId);
  assertTextFrame(UUID.test(event.commandId));
  const key = event.commandId;
  if (event.status === "started") {
    if (event.operation !== undefined) assertTextFrame(businessOperation(event) !== null);
    startExecution(state, key, "business");
    return onExecution(businessFrame(event));
  }
  if (event.status === "unknown") {
    onExecution(businessFrame(event));
    throw new BusinessOperationExecutionUnconfirmedError();
  }
  assertTextFrame(state.pending.get(key) === "business");
  if (event.status === "created") {
    assertBusinessResource({ operation: businessOperation(event), resource: event.resource });
    state.committedBusiness = true;
  } else {
    assertTextFrame(event.status === "rejected");
  }
  state.pending.delete(key);
  onExecution(businessFrame(event));
}

function unconfirmedExecution(state: ExecutionState): Error | null {
  if (state.committedDocument || [...state.pending.values()].some((kind) => kind === "document"))
    return new DocumentExecutionUnconfirmedError();
  if (state.committedBusiness || state.pending.size)
    return new BusinessOperationExecutionUnconfirmedError();
  return null;
}

function hasTextEnvelope(event: Extract<TextStreamEvent, { type: "final" }>): boolean {
  if (typeof event.messageId !== "string" || !event.messageId) return false;
  return (
    typeof event.messageEnvelope === "string" &&
    event.messageEnvelope.length > 0 &&
    event.messageEnvelope.length <= 4096
  );
}

type TextAnswerState = {
  conversationId: string;
  text: string;
  onText: (text: string) => void;
  onExecution: (frame: BusinessExecutionFrame) => void;
  execution: ExecutionState;
  final?: Extract<TextStreamEvent, { type: "final" }>;
};

function finalTextAnswer(
  event: TextStreamEvent,
  state: TextAnswerState,
): Extract<TextStreamEvent, { type: "final" }> {
  assertTextFrame(event.type === "final");
  assertFinalContent(event, state);
  assertTextFrame(hasTextEnvelope(event));
  if (state.execution.pending.size) {
    const unconfirmed = unconfirmedExecution(state.execution);
    if (unconfirmed) throw unconfirmed;
  }
  state.final = event;
  return event;
}

function assertFinalContent(
  event: Extract<TextStreamEvent, { type: "final" }>,
  state: TextAnswerState,
): void {
  assertTextFrame(event.isFinal === true);
  assertTextFrame(event.conversationId === state.conversationId);
  assertTextFrame(event.text === state.text && state.text.trim());
}

function appendText(
  event: Extract<TextStreamEvent, { type: "delta" }>,
  state: TextAnswerState,
): void {
  assertTextFrame(typeof event.text === "string");
  state.text += event.text;
  if (state.text.length > 16_000) throw new Error("Answer too large");
  state.onText(state.text);
}

function applyTextEvent(
  event: TextStreamEvent,
  state: TextAnswerState,
): Extract<TextStreamEvent, { type: "final" }> | void {
  if (event.type === "assistantDocumentExecution")
    return trackDocumentExecution(event, state.conversationId, state.execution);
  if (event.type === "assistantBusinessExecution")
    return trackBusinessExecution(event, state.conversationId, state.execution, state.onExecution);
  if (event.type === "reset") {
    state.text = "";
    return state.onText(state.text);
  }
  if (event.type === "delta") return appendText(event, state);
  if (event.type === "assistantDocumentDraft") {
    assertTextFrame(event.conversationId === state.conversationId);
    return; // The owned-conversation query supplies the proposal itself.
  }
  return finalTextAnswer(event, state);
}

export async function consumeTextStream(
  response: Response,
  signal: AbortSignal,
  conversationId: string,
  onText: (text: string) => void,
  onExecution: (frame: BusinessExecutionFrame) => void = () => undefined,
): Promise<Extract<TextStreamEvent, { type: "final" }>> {
  const state: TextAnswerState = {
    conversationId,
    onText,
    onExecution,
    text: "",
    execution: {
      committedBusiness: false,
      committedDocument: false,
      rejectedDocument: false,
      pending: new Map(),
    },
  };
  try {
    for await (const data of readSseData(response, signal)) {
      // Read to EOF after the final envelope instead of cancelling the body, so
      // a completed response is never reported as aborted. Trailing frames
      // cannot replace the final envelope.
      if (!state.final) applyTextEvent(JSON.parse(data) as TextStreamEvent, state);
    }
    if (state.final) return state.final;
    throw new Error("Incomplete text answer");
  } catch (error) {
    if (state.final) return state.final;
    // A transport failure after dispatch cannot establish whether the DB committed.
    const unconfirmed = unconfirmedExecution(state.execution);
    if (unconfirmed) throw unconfirmed;
    if (state.execution.rejectedDocument) throw new DocumentExecutionRejectedError();
    throw error;
  }
}
